import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { OrganizationSchema, WebsiteSchema, ArticleSchema, BreadcrumbSchema } from '@/components/feature/SchemaOrg';
import { newsArticles } from '@/mocks/homeData';

const categoryLabels: Record<string, string> = {
  football: '足球',
  basketball: '篮球',
  esports: '电竞',
  racing: '赛车',
  tennis: '网球',
  swimming: '游泳',
};

const detailImages: Record<number, string> = {
  1: 'https://readdy.ai/api/search-image?query=Champions%20League%20football%20stadium%20with%20dramatic%20floodlights%20and%20players%20in%20action%2C%20epic%20atmosphere%20with%20crowd%20cheering%2C%20professional%20sports%20photography%20with%20warm%20golden%20tones&width=1200&height=600&seq=article-detail-1&orientation=landscape',
  2: 'https://readdy.ai/api/search-image?query=NBA%20basketball%20game%20intense%20moment%20two%20superstar%20players%20facing%20off%2C%20arena%20crowd%20energy%2C%20dramatic%20sports%20photography%20with%20motion%20and%20spotlights&width=1200&height=600&seq=article-detail-2&orientation=landscape',
  3: 'https://readdy.ai/api/search-image?query=Chinese%20womens%20volleyball%20team%20celebrating%20victory%20on%20the%20court%2C%20dynamic%20action%20shot%20athletes%20jumping%20spiking%20with%20red%20flags&width=1200&height=600&seq=article-detail-3&orientation=landscape',
  4: 'https://readdy.ai/api/search-image?query=Formula%20One%20racing%20car%20speeding%20on%20track%20with%20extreme%20motion%20blur%2C%20dramatic%20skies%2C%20professional%20motorsport%20photography%20with%20vibrant%20colors&width=1200&height=600&seq=article-detail-4&orientation=landscape',
  5: 'https://readdy.ai/api/search-image?query=Esports%20arena%20with%20massive%20LED%20screens%20and%20packed%20audience%2C%20gaming%20competition%20stage%20with%20dramatic%20cyan%20and%20purple%20lights&width=1200&height=600&seq=article-detail-5&orientation=landscape',
  6: 'https://readdy.ai/api/search-image?query=Professional%20swimmer%20in%20olympic%20pool%20doing%20powerful%20breaststroke%2C%20water%20splashes%20intense%20motion%2C%20bright%20stadium%20lighting&width=1200&height=600&seq=article-detail-6&orientation=landscape',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);
  const article = newsArticles.find((a) => a.id === articleId) || newsArticles[0];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const relatedArticles = newsArticles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const catLabel = categoryLabels[article.category] || article.category;

  return (
    <>
      <OrganizationSchema />
      <WebsiteSchema />
      <ArticleSchema articleId={article.id} />
      <BreadcrumbSchema items={[
        { name: '首页', url: '/' },
        { name: catLabel, url: `/category/${article.category}` },
        { name: article.title, url: `/article/${article.id}` },
      ]} />
      <Navbar />
      <main className="pt-16">
        <article className="w-full py-8 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-[900px] mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <Link to={`/category/${article.category}`} className="hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">{catLabel}</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-700 font-medium line-clamp-1">{article.title}</span>
              </nav>

              <header className="mb-8">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <span className="bg-primary-100 text-primary-700 text-xs font-medium px-2.5 py-1 rounded-full">{catLabel}</span>
                  {article.tags.map((tag) => (
                    <span key={tag} className="bg-background-100 text-foreground-500 text-xs px-2.5 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                  <span><i className="ri-user-line mr-1"></i>{article.author}</span>
                  <span><i className="ri-time-line mr-1"></i>{article.date}</span>
                  <span><i className="ri-eye-line mr-1"></i>{(article.views / 10000).toFixed(1)}万阅读</span>
                </div>
              </header>

              <div className="relative h-64 md:h-96 rounded-xl overflow-hidden mb-8">
                <img
                  src={detailImages[article.id]}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose max-w-none text-foreground-700 leading-relaxed space-y-4 text-base"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span key={tag} className="bg-background-100 text-foreground-600 text-xs font-medium px-3 py-1.5 rounded-full cursor-pointer hover:bg-primary-100 hover:text-primary-600 transition-colors">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-[1280px] mx-auto">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">相关文章</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 overflow-hidden transition-all duration-300 cursor-pointer"
                    >
                      <div className="p-4 md:p-5">
                        <span className="bg-primary-100 text-primary-700 text-xs font-medium px-2.5 py-1 rounded-full mb-3 inline-block">
                          {categoryLabels[ra.category] || ra.category}
                        </span>
                        <h3 className="text-sm font-semibold text-foreground-900 leading-snug group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">{ra.title}</h3>
                        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">{ra.summary}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}